import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Button from '@/components/CustomButtons/Button';
import { useTranslation } from 'react-i18next';

const sectionSx = { mt: 3 };

const sectionTitleSx = {
  fontWeight: 600,
  fontSize: '0.95rem',
  mb: 1,
};

const statsGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
  gap: 1.5,
};

const statTileSx = {
  border: '1px solid #e0e0e0',
  borderRadius: '6px',
  padding: '10px 12px',
  backgroundColor: '#fafafa',
};

const CODE_KIND_COLORS = {
  paid: 'success',
  unknown: 'warning',
};

// The endpoint's shape varies (and older responses have no `data` at all), so
// every field is coerced before it reaches the render tree.
const asObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const asRecords = (value) =>
  Array.isArray(value)
    ? value.filter((item) => item && typeof item === 'object')
    : [];

const asText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatAmount = (amount) => {
  const parsed = parseFloat(amount);
  if (Number.isNaN(parsed)) return '-';
  return `$${parsed.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const StatTile = ({ label, value, highlight }) => (
  <Box sx={statTileSx}>
    <Typography
      sx={{ fontSize: '0.7rem', color: '#757575', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '1.15rem',
        fontWeight: 600,
        color: highlight ? `${highlight}.main` : 'text.primary',
      }}>
      {value}
    </Typography>
  </Box>
);

const ReconProcessingResultModal = ({ result, onClose }) => {
  const { t } = useTranslation();

  if (!result) return null;

  const body = asObject(result);
  const data = asObject(body.data);
  const summary = asObject(data.summary);
  const totals = asObject(summary.totals);
  const applied = asObject(summary.applied);
  const files = asRecords(summary.files);
  const codeBreakdown = asRecords(summary.codeBreakdown);
  const errors = Array.isArray(summary.errors)
    ? summary.errors.filter((error) => error !== null && error !== undefined)
    : [];

  const skipCounts = Object.entries(asObject(summary.skipCounts))
    .filter(([, count]) => Number(count) > 0)
    .sort(([, a], [, b]) => Number(b) - Number(a));

  const uploadedNames = asObject(summary.uploaded).fileNames;
  const hasSummary = Object.keys(summary).length > 0;
  const isSuccess = !hasSummary || summary.status === 'success';
  const message = asText(
    summary.message || body.msg || (typeof result === 'string' ? result : ''),
  );
  const fileName = asText(
    data.filename || (Array.isArray(uploadedNames) ? uploadedNames[0] : ''),
  );

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isSuccess ? (
            <CheckCircleIcon color='success' />
          ) : (
            <WarningAmberIcon color='warning' />
          )}
          {t('reconProcessingSmartPayables.result.title')}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {message && <Typography>{message}</Typography>}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {fileName && (
            <Chip size='small' variant='outlined' label={fileName} />
          )}
          {summary.decrypted && (
            <Chip
              size='small'
              color='info'
              variant='outlined'
              label={t('reconProcessingSmartPayables.result.decrypted')}
            />
          )}
          {summary.emailSent && (
            <Chip
              size='small'
              color='info'
              variant='outlined'
              label={t('reconProcessingSmartPayables.result.emailSent')}
            />
          )}
        </Box>

        {hasSummary && (
          <Box sx={sectionSx}>
            <Box sx={statsGridSx}>
              <StatTile
                label={t(
                  'reconProcessingSmartPayables.result.stats.parsedRows',
                )}
                value={asText(totals.parsedRows ?? 0)}
              />
              <StatTile
                label={t('reconProcessingSmartPayables.result.stats.applied')}
                value={asText(applied.applied ?? data.applied_count ?? 0)}
              />
              <StatTile
                label={t(
                  'reconProcessingSmartPayables.result.stats.skippedRows',
                )}
                value={asText(totals.skippedRows ?? 0)}
                highlight={Number(totals.skippedRows) > 0 ? 'warning' : null}
              />
              <StatTile
                label={t('reconProcessingSmartPayables.result.stats.failed')}
                value={asText(applied.failed ?? 0)}
                highlight={Number(applied.failed) > 0 ? 'error' : null}
              />
              <StatTile
                label={t('reconProcessingSmartPayables.result.stats.anomalies')}
                value={asText(totals.anomalyCount ?? 0)}
                highlight={Number(totals.anomalyCount) > 0 ? 'warning' : null}
              />
              <StatTile
                label={t(
                  'reconProcessingSmartPayables.result.stats.refundsProcessed',
                )}
                value={asText(data.refunds_processed ?? 0)}
              />
              <StatTile
                label={t(
                  'reconProcessingSmartPayables.result.stats.totalAmount',
                )}
                value={formatAmount(
                  totals.totalAmount ?? data.processed_amount ?? 0,
                )}
              />
            </Box>
          </Box>
        )}

        {codeBreakdown.length > 0 && (
          <Box sx={sectionSx}>
            <Typography sx={sectionTitleSx}>
              {t('reconProcessingSmartPayables.result.codeBreakdown.title')}
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t(
                        'reconProcessingSmartPayables.result.codeBreakdown.code',
                      )}
                    </TableCell>
                    <TableCell>
                      {t(
                        'reconProcessingSmartPayables.result.codeBreakdown.meaning',
                      )}
                    </TableCell>
                    <TableCell align='right'>
                      {t(
                        'reconProcessingSmartPayables.result.codeBreakdown.rows',
                      )}
                    </TableCell>
                    <TableCell align='right'>
                      {t(
                        'reconProcessingSmartPayables.result.codeBreakdown.applied',
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {codeBreakdown.map((code, index) => (
                    <TableRow key={`${asText(code.rawCode)}-${index}`}>
                      <TableCell>
                        <Chip
                          size='small'
                          label={asText(code.rawCode)}
                          color={CODE_KIND_COLORS[code.codeKind] || 'default'}
                        />
                      </TableCell>
                      <TableCell>{asText(code.codeLabel)}</TableCell>
                      <TableCell align='right'>{asText(code.count)}</TableCell>
                      <TableCell align='right'>
                        {asText(code.appliedCount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {skipCounts.length > 0 && (
          <Box sx={sectionSx}>
            <Typography sx={sectionTitleSx}>
              {t('reconProcessingSmartPayables.result.skipped.title')}
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('reconProcessingSmartPayables.result.skipped.reason')}
                    </TableCell>
                    <TableCell align='right'>
                      {t('reconProcessingSmartPayables.result.skipped.rows')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skipCounts.map(([reason, count]) => (
                    <TableRow key={reason}>
                      <TableCell sx={{ textTransform: 'capitalize' }}>
                        {reason}
                      </TableCell>
                      <TableCell align='right'>{asText(count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {files.length > 1 && (
          <Box sx={sectionSx}>
            <Typography sx={sectionTitleSx}>
              {t('reconProcessingSmartPayables.result.files.title')}
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('reconProcessingSmartPayables.result.files.fileName')}
                    </TableCell>
                    <TableCell>
                      {t('reconProcessingSmartPayables.result.files.status')}
                    </TableCell>
                    <TableCell align='right'>
                      {t(
                        'reconProcessingSmartPayables.result.stats.parsedRows',
                      )}
                    </TableCell>
                    <TableCell align='right'>
                      {t('reconProcessingSmartPayables.result.stats.applied')}
                    </TableCell>
                    <TableCell align='right'>
                      {t(
                        'reconProcessingSmartPayables.result.stats.skippedRows',
                      )}
                    </TableCell>
                    <TableCell align='right'>
                      {t(
                        'reconProcessingSmartPayables.result.stats.totalAmount',
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((processedFile, index) => (
                    <TableRow
                      key={`${asText(processedFile.reconFileId)}-${index}`}>
                      <TableCell>{asText(processedFile.fileName)}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>
                        {asText(processedFile.status)}
                      </TableCell>
                      <TableCell align='right'>
                        {asText(processedFile.parsedRows ?? 0)}
                      </TableCell>
                      <TableCell align='right'>
                        {asText(processedFile.appliedCount ?? 0)}
                      </TableCell>
                      <TableCell align='right'>
                        {asText(processedFile.skippedRows ?? 0)}
                      </TableCell>
                      <TableCell align='right'>
                        {formatAmount(processedFile.totalAmount ?? 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {errors.length > 0 && (
          <Alert severity='error' sx={sectionSx}>
            <AlertTitle>
              {t('reconProcessingSmartPayables.result.errors.title')}
            </AlertTitle>
            {errors.map((error, index) => (
              <Typography key={index} sx={{ fontSize: '0.85rem' }}>
                {asText(asObject(error).message || error)}
              </Typography>
            ))}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button color='primary' onClick={onClose}>
          {t('reconProcessingSmartPayables.result.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReconProcessingResultModal;
